import { AccessTime as AccessTimeIcon, Schedule as ScheduleIcon } from "@mui/icons-material";
import { Alert, Avatar, Box, Card, CardContent, CircularProgress, Fade, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useDispatch } from "react-redux";
import { gateway } from "../gateway";
import { updateSignupEmployeeState } from "../redux/features/signupEmployeeStateSlice";
import { updateUser } from "../redux/features/userStateSlice";
import { LoaderAnimationComponent } from "./common/LoaderAnimationComponent";
import { SnackbarAlert } from "./common/SnackbarAlert";
import { Schedule } from "./schedule";
import { colors } from "./ui/colors";
import { ButtonsHoursOperation } from "./ButtonsHoursOperation";

export const HoursOfOperation = ({ updateComponentState = updateSignupEmployeeState, sx }) => {
  const dispatch = useDispatch();
  const tokenLocal = sessionStorage.getItem("token");

  const [availability, setMyAvailability] = useState();
  const [changes, setChanges] = useState(false);
  const [cancel, setCancel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isScheduleValid, setIsScheduleValid] = useState(true);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  const { showBoundary } = useErrorBoundary();

  const getMyAvailability = async () => {
    try {
      const { data } = await gateway.get("/availability/self", {
        headers: { Authorization: tokenLocal },
      });
      dispatch(updateUser({ availability: data.data.availability }));
      setMyAvailability(data.data.availability);
    } catch (error) {
      console.error(error);
      showBoundary(error);
    }
  };

  const saveMyAvailability = async () => {
    try {
      const { data } = await gateway.put("/availability/self", { availability }, { headers: { Authorization: tokenLocal } });
      setMyAvailability(data.data);
      setAlertMessage("Availability updated successfully");
      setAlertSeverity("success");
      setChanges(false);
    } catch (error) {
      console.error(error);
      setAlertMessage("Error updating availability");
      setAlertSeverity("error");
    } finally {
      setOpenAlert(true);
    }
  };

  const handleAvailabilityInfo = async () => {
    setLoading(true);
    setIsVisible(false);
    await getMyAvailability();
    setTimeout(() => setIsVisible(true), 100);
    setLoading(false);
  };

  useEffect(() => {
    handleAvailabilityInfo();
  }, []);

  useEffect(() => {
    if (!cancel) return;
    handleAvailabilityInfo();
    setCancel(false);
    setChanges(false);
  }, [cancel]);

  const selectedSlotsCount = useMemo(() => {
    if (!availability) return 0;
    return Object.values(availability).reduce((total, daySlots) => {
      return Array.isArray(daySlots) ? total + daySlots.length : total;
    }, 0);
  }, [availability]);

  return (
    <>
      <Fade in={!loading} timeout={300}>
        <Box
          sx={{
            width: "100%",
            px: { xs: 3, sm: 4 },
            py: { xs: 3, sm: 4 },
            opacity: isVisible ? 1 : 0,
            ...sx,
          }}
        >
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                backgroundColor: colors.orange[100],
                mx: "auto",
                mb: 2,
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 28, color: colors.orange.base }} />
            </Avatar>

            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}>
              Hours of Operation
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                maxWidth: 500,
                mx: "auto",
              }}
            >
              Configure your operating hours. You can type times manually or use the clock button to select them.
            </Typography>

            {selectedSlotsCount > 0 && (
              <Box sx={{ mt: 2, px: 2, py: 1, backgroundColor: "var(--fx-colors-info-50, #EFF6FF)", borderRadius: 20 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "#1976d2" }}>
                  {selectedSlotsCount} time slots configured
                </Typography>
              </Box>
            )}
          </Box>

          <Card
            elevation={0}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: 2,
              width: "100%",
              backgroundColor: "var(--fx-surface-base, #FFFFFF)",
              minHeight: 300,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              {availability ? (
                <Stack spacing={3}>
                  <Alert
                    severity="info"
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                    }}
                  >
                    Enter hours in HH:mm format (example: 09:00 or 17:30).
                  </Alert>
                  <Schedule
                    updateComponentState={updateComponentState}
                    setChanges={setChanges}
                    checkedState={availability}
                    onAvailabilityChange={(hours, valid) => {
                      setMyAvailability(hours);
                      setIsScheduleValid(valid);
                    }}
                  />
                  {!isScheduleValid && <Alert severity="error">Please correct all schedule errors before saving.</Alert>}
                  <ButtonsHoursOperation disabled={!changes || !isScheduleValid} setCancel={setCancel} onSubmit={saveMyAvailability} />
                </Stack>
              ) : (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <ScheduleIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    Loading availability...
                  </Typography>
                  <CircularProgress size={24} />
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Fade>

      <LoaderAnimationComponent open={loading} />

      <SnackbarAlert openState={openAlert} setOpenState={setOpenAlert} severity={alertSeverity} duration={4500} message={alertMessage} />
    </>
  );
};
